import { BarChart } from '@mui/x-charts/BarChart';
import dayjs from 'dayjs';
import { CommandDashboardLoginActivityDailyCounts } from '../../api/command-dashboard-api';
import { colors } from '../../theme/colors';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';

interface LoginActivityGraphProps {
  dailyCounts: CommandDashboardLoginActivityDailyCounts[];
  // Layout of the graph wrapper (width, flex). Chart height is set on BarChart itself.
  sx?: SxProps<Theme>;
}

// Heat-maps each day's bar by its share of the week's volume - matches the Figma spec, which
// shades busier days darker rather than using one flat color for every bar.
// Figma's y-label column is 22px wide with the plot starting 26px in. Left
// unset, MUI sizes the axis to fit its tick labels, which runs wider.
const Y_AXIS_WIDTH = 30;

const BAR_LEVEL_COLORS = [
  colors.primary[200],
  colors.primary[300],
  colors.primary[400],
  colors.primary[600],
];

export default function LoginActivityGraph({
  dailyCounts,
  sx,
}: LoginActivityGraphProps) {
  const sortedDays = [...dailyCounts].sort((a, b) =>
    a.date.localeCompare(b.date)
  );
  const values = sortedDays.map((day) => day.totalLogins);
  const labels = sortedDays.map((day) => dayjs(day.date).format('ddd'));
  const max = Math.max(...values, 1);
  const thresholds = [max * 0.25, max * 0.5, max * 0.75];

  return (
    <Stack sx={[{ minWidth: 100 }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <BarChart
        // Sized so the chart and its caption fit the height a tile's size
        // allows for them, the tightest being the 340px full-width one.
        height={180}
        // The right margin matches the y-axis width so the plot sits centred
        // in the graph's box, lining up with the caption underneath. Figma
        // draws the plot flush to the box's right edge instead, which left the
        // bars reading as off-centre by the width of the axis labels.
        margin={{ top: 16, right: Y_AXIS_WIDTH, bottom: 26, left: 0 }}
        series={[{ data: values }]}
        xAxis={[
          {
            scaleType: 'band',
            data: labels,
            categoryGapRatio: 0.5,
            // Even the narrowest real tile width this chart is ever measured at leaves well over
            // 40px per category - always plenty for a 3-letter day abbreviation - so every label
            // can safely always render rather than being left to MUI's own overlap-avoidance
            // heuristic, which hid labels inconsistently (see git history on this file).
            tickLabelInterval: () => true,
          },
        ]}
        yAxis={[
          {
            width: Y_AXIS_WIDTH,
            colorMap: {
              type: 'piecewise',
              thresholds,
              colors: BAR_LEVEL_COLORS,
            },
            valueFormatter: (value: number) =>
              value >= 1000 ? `${value / 1000}k` : `${value}`,
          },
        ]}
        borderRadius={4}
        grid={{ horizontal: true }}
        hideLegend
        slotProps={{ tooltip: { trigger: 'item' } }}
      />
      <Typography
        variant="caption"
        sx={{
          textAlign: 'center',
          color: (theme) => theme.palette.semantic.text.title2,
        }}
      >
        Number of logins per day
      </Typography>
    </Stack>
  );
}
